import React, { createContext, PropsWithChildren, useContext, useEffect, useCallback } from 'react';
import { AxiosError } from 'axios';
import { useAuth } from '../authContext/AuthContext';
import AppInsightsContextType from '../../types/AppInsightContext.type';
import * as appInsightUtils from '../../utils/AppInsightUtils';

const AppInsightsContext = createContext<AppInsightsContextType | undefined>(
    undefined,
);

export const AppInsightProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const userAuth = useAuth();
    const userDatas = userAuth?.userData;

    // Memoize trackPageView
    const trackPageView = useCallback((pageName: string) => {
        appInsightUtils.trackPageView(
            pageName,
            userDatas?.profile?.email as string,
        );
    }, [userDatas?.profile?.email]);

    // Memoize trackEvent
    const trackEvent = useCallback((eventName: string) => {
        appInsightUtils.trackEvent(eventName, userDatas?.profile?.email as string);
    }, [userDatas?.profile?.email]);

    // Memoize trackError
    const trackError = useCallback((error: AxiosError | Error) => {
        appInsightUtils.trackError(error);
    }, []);

    // Memoize logInformation
    const logInformation = useCallback(
        (message: string, moduleName: string, functionName: string) => {
            appInsightUtils.logInformation(message, moduleName, functionName);
        },
        [],
    );

    // Memoize logError
    const logError = useCallback(
        (
            message: string,
            moduleName: string,
            functionName: string,
            customProperties: object,
        ) => {
            appInsightUtils.logError(
                message,
                moduleName,
                functionName,
                customProperties,
            );
        },
        [],
    );

    useEffect(() => {
        if (userDatas && !userAuth?.isLoading) {
            appInsightUtils.setAuthenticatedUser(userDatas?.profile?.email as string);
        }
    }, [userDatas, userAuth?.isLoading]); // Added 'userDatas' explicitly

    const contextValue = React.useMemo(
        () => ({
            trackPageView,
            trackEvent,
            trackError,
            logInformation,
            logError,
        }),
        [trackPageView, trackEvent, trackError, logInformation, logError], // Correct dependencies
    );

    return (
        <AppInsightsContext.Provider value={contextValue}>
            {children}
        </AppInsightsContext.Provider>
    );
};

export const useAppInsightConsumer = () => {
    const context = useContext(AppInsightsContext);

    if (!context) {
        throw new Error(
            'useAppInsightConsumer must be used within a AppInsightsContext',
        );
    }
    return context;
};